import React, { useEffect, useMemo, useState } from 'react'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { BasicDropdown } from 'src/components/Reusable/BasicDropdown/BasicDropdown'
import {
  BASIC_DROPDOWN_ALIGN,
  BASIC_DROPDOWN_DROP,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.constants'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useBasicDropdown } from 'src/components/Reusable/BasicDropdown/context/BasicDropdownContext'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import isFunction from 'lodash/isFunction'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useHasCustomControlStatuses } from 'src/context/Gatekeeper'
import palette from 'src/designSystem/variables/palette'
import {
  ControlStatusDropdownOptions,
  ControlStatusOptions,
} from './Dropdowns.constants'

export const ControlStatusDropdown = ({
  control,
  initialStatus,
  onSelect,
  isDisabled,
}: {
  control: Control.AsObject
  initialStatus: CONTROL_STATUS
  onSelect?: (
    status: CONTROL_STATUS,
    customStatusId?: string,
    name?: string,
  ) => void
  isDisabled?: boolean
}): JSX.Element => {
  const { openDropdownId, setOpenDropdownId } = useBasicDropdown()
  const dropdownId = `control-status-${control.modelId}`
  const isOpen = openDropdownId === dropdownId
  const canUpdateStatus = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_UPDATE_CONTROL_STATUS,
    control,
  )
  const { data: customStatuses } = useControlStatuses()
  const hasCustomControlStatuses = useHasCustomControlStatuses()
  const isDropdownisabled = !canUpdateStatus || isDisabled

  const [selectedItem, setSelectedItem] = useState<BasicDropdownItem>(() => {
    const customStatus = hasCustomControlStatuses
      ? customStatuses
          ?.getItemsList()
          .find((s) => s.getId() === control.customStatusId)
      : undefined

    const customStatusName = customStatus?.getName()

    if (
      hasCustomControlStatuses &&
      customStatus &&
      customStatusName &&
      control.customStatusId
    ) {
      return {
        name: customStatusName,
        value: customStatus.getId(),
      }
    }
    return {
      name: ControlStatusOptions[initialStatus as CONTROL_STATUS],
      value: initialStatus,
    }
  })

  useEffect(() => {
    const customStatus = customStatuses
      ?.getItemsList()
      .find((s) => s.getId() === control.customStatusId)
    const customStatusName = customStatus?.getName()

    if (
      hasCustomControlStatuses &&
      customStatus &&
      customStatusName &&
      control.customStatusId
    ) {
      setSelectedItem({
        name: customStatusName,
        value: customStatus.getId(),
      })
    } else {
      setSelectedItem({
        name: ControlStatusOptions[initialStatus as CONTROL_STATUS],
        value: initialStatus,
      })
    }
  }, [
    customStatuses,
    control.customStatusId,
    initialStatus,
    hasCustomControlStatuses,
  ])

  const baseOptions = useMemo(
    () =>
      ControlStatusDropdownOptions.filter(
        (option) => option.key !== CONTROL_STATUS.NOTAPPLICABLE,
      ).map(
        (option) =>
          ({
            name: option.label,
            value: option.key,
          } as BasicDropdownItem),
      ),
    [],
  )

  const customOptions = useMemo(
    () =>
      hasCustomControlStatuses
        ? customStatuses
            ?.getItemsList()
            .filter((status) => status.getName())
            .map(
              (status) =>
                ({
                  name: status.getName(),
                  value: status.getId(),
                } as BasicDropdownItem),
            ) || []
        : [],
    [customStatuses, hasCustomControlStatuses],
  )

  const items = useMemo(() => {
    if (hasCustomControlStatuses) {
      return customOptions
    }
    return baseOptions
  }, [baseOptions, customOptions, hasCustomControlStatuses])

  const updateStatus = async (item: BasicDropdownItem) => {
    setSelectedItem(item)
    if (isFunction(onSelect)) {
      if (typeof item.value === 'string') {
        onSelect(control.status, item.value, item.name as string)
      } else {
        onSelect(item.value as CONTROL_STATUS, '', item.name as string)
      }
    }
  }

  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      setOpenDropdownId(dropdownId)
    } else {
      setOpenDropdownId(null)
    }
  }

  const selectedStatusColors = useMemo(() => {
    if (typeof selectedItem.value === 'string' && hasCustomControlStatuses) {
      const customStatus = customStatuses
        ?.getItemsList()
        .find((s) => s.getId() === selectedItem.value)
      if (customStatus?.getColor()) {
        return {
          background: customStatus.getColor(),
          border: customStatus.getBorderColor() || customStatus.getColor(),
        }
      }
    }
    const option = ControlStatusDropdownOptions.find(
      (option) => option.key === selectedItem.value,
    )
    return (
      option?.colors || {
        background: palette.neutral.white,
        border: palette.neutral['300'],
      }
    )
  }, [selectedItem.value, hasCustomControlStatuses, customStatuses])

  return (
    <BasicDropdown
      selectedItem={selectedItem}
      items={items}
      onSelect={updateStatus}
      drop={BASIC_DROPDOWN_DROP.DOWN}
      align={BASIC_DROPDOWN_ALIGN.END}
      backgroundColor={selectedStatusColors.background}
      borderColor={selectedStatusColors.border}
      onToggle={handleToggle}
      show={isOpen}
      offset={4}
      disabled={
        isDropdownisabled || selectedItem.value === CONTROL_STATUS.NOTAPPLICABLE
      }
    />
  )
}
